import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { SiteWithCategory } from "@/types/nav";
import { getTone } from "@/lib/tone";
import { formatVisits } from "@/lib/navData";

export default function FeaturedCarousel({ sites }: { sites: SiteWithCategory[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (sites.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % sites.length);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [sites.length]);

  if (sites.length === 0) return null;

  const go = (next: number) => {
    setIndex((next + sites.length) % sites.length);
  };

  const site = sites[index];
  const tone = getTone(site.tone);

  return (
    <div className="relative">
      <div className="rounded-2xl border border-background-200 bg-background-50 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          <div className="lg:col-span-3 p-6 md:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                <i className="ri-star-smile-line" />
                编辑推荐
              </span>
              <Link
                to={`/category/${encodeURIComponent(site.categoryName)}`}
                className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {site.categoryName}
              </Link>
            </div>

            <h3 className="font-heading font-extrabold text-2xl md:text-3xl text-foreground-950">
              {site.name}
            </h3>
            <p className="text-sm md:text-base text-foreground-600 mt-3 leading-relaxed">
              {site.description}
            </p>

            <div className="flex flex-wrap items-center gap-2 mt-4">
              {site.tags.map((tag) => (
                <span key={tag} className="text-xs px-2 py-0.5 rounded bg-background-100 text-foreground-500">
                  {tag}
                </span>
              ))}
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-fire-line text-accent-500" />
                {formatVisits(site.visits)} 次访问
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <a
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                立即访问
                <i className="ri-external-link-line ml-1" />
              </a>
              <Link
                to={`/detail/${site.id}`}
                className="px-5 py-2.5 rounded-md border border-background-200 text-sm text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解详情
              </Link>
            </div>
          </div>

          <div className={`lg:col-span-2 relative bg-gradient-to-br ${tone.gradient} min-h-[220px] flex items-center justify-center`}>
            <div className="absolute top-6 left-6 w-16 h-16 rounded-full bg-background-50/15" />
            <div className="absolute bottom-8 right-8 w-24 h-24 rounded-full bg-background-50/10" />
            <span className="relative font-heading font-extrabold text-background-50 text-6xl md:text-7xl">
              {site.logoText}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-4 mt-5">
        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一个推荐"
          className="w-9 h-9 rounded-full border border-background-200 bg-background-50 text-foreground-600 hover:text-primary-600 hover:border-primary-300 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <div className="flex items-center gap-2">
          {sites.map((item, i) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`切换到推荐 ${i + 1}`}
              className={`h-2 rounded-full transition-all cursor-pointer ${
                i === index ? "w-6 bg-primary-500" : "w-2 bg-background-300 hover:bg-background-400"
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一个推荐"
          className="w-9 h-9 rounded-full border border-background-200 bg-background-50 text-foreground-600 hover:text-primary-600 hover:border-primary-300 flex items-center justify-center transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>
    </div>
  );
}